import { useMemo, useState } from 'react';
import { Card } from '../../../../layout/Card.jsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { useAuth } from '../../../../shared/useAuth.js';
import { useRevenues } from '../../hooks/useRevenues.js';
import { useRevenueCategories } from '../../hooks/useRevenueCategories.js';
import { useCashboxes } from '../../../cashbox/hooks/useCashboxes.js';
import { RevenueRow } from './RevenueRow.jsx';
import { RevenueForm } from './RevenueForm.jsx';
import { RevenueDetails } from './RevenueDetails.jsx';
import { formatIQD } from '../../../../shared/format.js';
import { ViewAllToggle, ViewAllNotice } from '../../../../shared/ViewAllToggle.jsx';
import { listParams } from '../../../../shared/viewAll.js';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function startOfMonthISO() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

const SOURCES = [
  { key: '', label: 'الكل' },
  { key: 'manual', label: 'يدوي' },
  { key: 'sales', label: 'بيع تلقائي' },
  { key: 'session_exit', label: 'إنهاء جلسة' },
  { key: 'card_topup', label: 'تعبئة كارت' },
  { key: 'other', label: 'أخرى' }
];

export function RevenuesList() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canCreate = hasPermission('finance.revenue.create');

  const { data: categories = [] } = useRevenueCategories();
  const { data: cashboxes = [] } = useCashboxes();

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [from, setFrom] = useState(startOfMonthISO());
  const [to, setTo] = useState(todayISO());
  const [source, setSource] = useState('');
  const [cashboxId, setCashboxId] = useState('');
  const [page, setPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [viewing, setViewing] = useState(null);

  const params = useMemo(
    () => ({
      // الخادم يسمّي حقل البحث q — إرسال search كان يُهمل بصمت.
      q: search || undefined,
      categoryId: categoryId || undefined,
      from,
      to,
      source: source || undefined,
      cashboxId: cashboxId || undefined,
      ...listParams({ all: showAll, page, limit: 20 })
    }),
    [search, categoryId, from, to, source, cashboxId, page, showAll]
  );

  const { data, isLoading, error } = useRevenues(params);
  const revenues = data?.revenues || data?.items || data?.data || [];
  // الخادم يعيد { items, total, page, pages, limit } مسطّحاً — لا كائن pagination.
  const pagination = data?.pagination || data?.meta || {
    page: data?.page || page,
    totalPages: data?.pages || 1,
    total: data?.total ?? 0
  };
  const totalAmount = data?.totalAmount ?? revenues.reduce((s, x) => s + (Number(x.amount) || 0), 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[15px] font-bold">الإيرادات</div>
          <div className="text-xs text-ink-3 mt-0.5">إجمالي الفترة: <b className="text-green">{formatIQD(totalAmount)}</b></div>
        </div>
        <div className="flex items-center gap-2">
          <ViewAllToggle
            all={showAll}
            onChange={(v) => { setShowAll(v); setPage(1); }}
            shown={revenues.length}
            total={pagination.total}
          />
        {canCreate ? (
          <button type="button" className="btn btn-primary" onClick={() => setFormOpen(true)}>
            <Icon name="plus" size={14} /> إيراد جديد
          </button>
        ) : null}
        </div>
      </div>

      <Card>
        <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">بحث</label>
            <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الفئة</label>
            <select className="input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">الكل</option>
              {categories.map((c) => (
                <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">من</label>
            <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">إلى</label>
            <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">المصدر</label>
            <select className="input" value={source} onChange={(e) => setSource(e.target.value)}>
              {SOURCES.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">القاصة</label>
            <select className="input" value={cashboxId} onChange={(e) => setCashboxId(e.target.value)}>
              <option value="">الكل</option>
              {cashboxes.map((c) => (
                <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {error ? (
        <Card><div className="text-red text-center py-4">تعذّر تحميل الإيرادات</div></Card>
      ) : isLoading ? (
        <Card><div className="text-ink-3 text-center py-8">جاري التحميل...</div></Card>
      ) : revenues.length === 0 ? (
        <Card>
          <div className="text-ink-3 text-center py-10 flex flex-col items-center gap-3">
            <Icon name="finance" size={42} color="#D6CCB3" />
            <div className="text-[14px] font-semibold text-ink-2">لا توجد إيرادات في هذه الفترة</div>
          </div>
        </Card>
      ) : (
        <Card>
          <table className="w-full text-[13px]">
            <thead className="text-ink-2 text-[12px]">
              <tr className="border-b border-line">
                <th className="p-2.5 text-start">التاريخ</th>
                <th className="p-2.5 text-start">الفئة</th>
                <th className="p-2.5 text-start">المبلغ</th>
                <th className="p-2.5 text-start">الاستلام</th>
                <th className="p-2.5 text-start">القاصة</th>
                <th className="p-2.5 text-start">مُنفّذ العملية</th>
                <th className="p-2.5 text-start">المصدر</th>
                <th className="p-2.5 text-start">الوصف</th>
                <th className="p-2.5 text-end">تفاصيل</th>
              </tr>
            </thead>
            <tbody>
              {revenues.map((r) => (
                <RevenueRow key={r._id || r.id} revenue={r} onView={(x) => setViewing(x)} />
              ))}
            </tbody>
          </table>

          {showAll ? (
            <ViewAllNotice all={showAll} shown={revenues.length} total={pagination.total} />
          ) : pagination.totalPages > 1 ? (
            <div className="flex items-center justify-between pt-3 border-t border-line mt-3">
              <div className="text-xs text-ink-3">صفحة {pagination.page || page} من {pagination.totalPages}</div>
              <div className="flex items-center gap-1.5">
                <button type="button" className="btn" style={{ padding: '6px 10px' }} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>السابق</button>
                <button type="button" className="btn" style={{ padding: '6px 10px' }} disabled={page >= (pagination.totalPages || 1)} onClick={() => setPage((p) => p + 1)}>التالي</button>
              </div>
            </div>
          ) : null}
        </Card>
      )}

      <RevenueForm open={formOpen} onClose={() => setFormOpen(false)} onSuccess={() => setPage(1)} />

      {viewing ? <RevenueDetails revenue={viewing} onClose={() => setViewing(null)} /> : null}
    </div>
  );
}
